import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Select, InputNumber, Switch, Input } from "antd";
import { useNotification } from "../../NotificationContext";
import { Editor } from "react-draft-wysiwyg";
import { EditorState, convertToRaw, convertFromRaw } from "draft-js";
import { ContentState } from "draft-js";
import {
  saveLabTest,
  updateLabTest,
  getLabTestById,
  getLabTestCategories,
} from "../../apis/LabOrder";
import { LabTest, LabTestSection, LabTestParameter, LabTestCategory, ReferenceRange } from "../../interfaces/LabTest";

const { Option } = Select;
const { TextArea } = Input;

type Errors = {
  name?: string;
  defaultCharge?: string;
  sections?: { [key: number]: { name?: string } };
  parameters?: { [key: number]: { name?: string } };
};

const AddTest = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { showNotification } = useNotification();
  const isEditMode = !!id;

  const [test, setTest] = useState<LabTest>({
    name: "",
    code: "",
    categoryId: null,
    defaultCharge: 0,
    description: "",
    sections: [],
    parameters: [],
  });
  const [descriptionEditorState, setDescriptionEditorState] = useState(
    EditorState.createEmpty()
  );
  const [categories, setCategories] = useState<LabTestCategory[]>([]);
  const [errors, setErrors] = useState<Errors>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchCategories();
    if (isEditMode && id) {
      fetchTestData(parseInt(id));
    }
  }, [id, isEditMode]);

  const fetchCategories = async () => {
    try {
      const response = await getLabTestCategories();
      setCategories(
        (response.data.data || response.data || []).sort(
          (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name)
        )
      );
    } catch (err) {
      console.error("Failed to fetch categories:", err);
    }
  };

  const transformParameterFromResponse = (param: any): LabTestParameter => {
    // Transform reference ranges array
    const referenceRanges: ReferenceRange[] = [];
    console.log("Reference ranges from API:", param);
    // API returns reference_ranges in snake_case, but also check camelCase for backward compatibility
    const ranges = param.referenceRanges || [];
    
    if (ranges && ranges.length > 0) {
      ranges.forEach((range: any) => {
        referenceRanges.push({
          id: range.id || undefined,
          gender: range.gender || null,
          ageMin: range.ageMin !== null && range.ageMin !== undefined ? Number(range.ageMin) : null,
          ageMax: range.ageMax !== null && range.ageMax !== undefined ? Number(range.ageMax) : null,
          rangeMin: range.rangeMin !== null && range.rangeMin !== undefined ? Number(range.rangeMin) : null,
          rangeMax: range.rangeMax !== null && range.rangeMax !== undefined ? Number(range.rangeMax) : null,
          categoryName: range.categoryName || null,
          remarks: range.remarks || null,
        });
      });
    }

    // Keep backward compatibility with old fields for display
    let referenceMin: number | null = null;
    let referenceMax: number | null = null;
    let referenceText: string | null = null;

    if (referenceRanges.length > 0) {
      const firstRange = referenceRanges[0];
      referenceMin = firstRange.rangeMin || null;
      referenceMax = firstRange.rangeMax || null;
      if (param.data_type === 'text' && firstRange.categoryName && !firstRange.rangeMin && !firstRange.rangeMax) {
        referenceText = firstRange.categoryName;
      }
    }

    return {
      id: param.id,
      labTestId: param.lab_test_id,
      labTestSectionId: param.lab_test_section_id || null,
      name: param.name || '',
      code: param.code || null,
      unit: param.unit || null,
      dataType: (param.data_type || 'numeric') as 'numeric' | 'text' | 'boolean' | 'date',
      referenceMin,
      referenceMax,
      referenceText,
      referenceRanges: referenceRanges.length > 0 ? referenceRanges : [],
      description: param.description || null,
      displayOrder: param.display_order || param.displayOrder || 0,
      isActive: param.is_active !== undefined ? param.is_active : true,
    };
  };

  const fetchTestData = async (testId: number) => {
    try {
      setLoading(true);
      const response = await getLabTestById(testId);
      const data = response.data.data || response.data;
      console.log({data});
      
      
      // Transform sections with their parameters
      const transformedSections = (data.sections || []).map((section: any) => ({
        id: section.id,
        labTestId: section.lab_test_id,
        name: section.name || '',
        description: section.description || null,
        displayOrder: section.display_order || section.displayOrder || 0,
        parameters: (section.parameters || []).map((param: any) => transformParameterFromResponse(param)),
      }));

      // Transform top-level parameters - only include those that don't belong to any section
      // (parameters with lab_test_section_id are already included in their sections)
      const transformedParameters = (data.parameters || [])
        .filter((param: any) => !param.lab_test_section_id)
        .map((param: any) => transformParameterFromResponse(param));

      setTest({
        id: data.id,
        clinicId: data.clinic_id,
        name: data.name || '',
        code: data.code || null,
        categoryId: data.category_id || data.categoryId || null,
        defaultCharge: parseFloat(data.default_charge || data.defaultCharge || '0'),
        description: data.description || null,
        sections: transformedSections,
        parameters: transformedParameters,
      });
      // Description editor
      // if (data.description) {
      //   const content = convertFromRaw(JSON.parse(data.description));
      //   setDescriptionEditorState(EditorState.createWithContent(content));
      // }
      if (data.description) {
        try {
          // try DraftJS JSON
          const raw = JSON.parse(data.description);
          const content = convertFromRaw(raw);
          setDescriptionEditorState(EditorState.createWithContent(content));
        } catch (e) {
          const content = ContentState.createFromText(data.description);
          setDescriptionEditorState(EditorState.createWithContent(content));
        }
      }
    } catch (err) {
      console.error("Failed to fetch test:", err);
      showNotification("error", "Failed to load test data", "", "topRight");
    } finally {
      setLoading(false);
    }
  };

  const updateTestField = (field: keyof LabTest, value: any) => {
    setTest({ ...test, [field]: value });
    // Clear error if field exists in errors
    if ((field === 'name' || field === 'defaultCharge') && errors[field]) {
      setErrors({ ...errors, [field]: undefined });
    }
  };

  const addSection = () => {
    const newSection: LabTestSection = {
      name: "",
      description: "",
      displayOrder: (test.sections?.length || 0) + 1,
      parameters: [],
    };
    setTest({
      ...test,
      sections: [...(test.sections || []), newSection],
    });
  };

  const updateSection = (index: number, field: keyof LabTestSection, value: any) => {
    const updatedSections = [...(test.sections || [])];
    updatedSections[index] = { ...updatedSections[index], [field]: value };
    setTest({ ...test, sections: updatedSections });
    
    // Clear error for this field if it exists
    if (errors.sections?.[index] && field === 'name') {
      const updatedErrors = { ...errors };
      if (updatedErrors.sections?.[index]) {
        const sectionErrors = { ...updatedErrors.sections[index] };
        delete sectionErrors.name;
        if (Object.keys(sectionErrors).length === 0) {
          const newSections = { ...updatedErrors.sections };
          delete newSections[index];
          updatedErrors.sections = Object.keys(newSections).length > 0 ? newSections : undefined;
        } else {
          updatedErrors.sections[index] = sectionErrors;
        }
      }
      setErrors(updatedErrors);
    }
  };

  const removeSection = (index: number) => {
    const updatedSections = test.sections?.filter((_, i) => i !== index) || [];
    // Update display orders
    updatedSections.forEach((section, i) => {
      section.displayOrder = i + 1;
    });
    setTest({ ...test, sections: updatedSections });
  };

  const addParameter = (sectionIndex?: number) => {
    const newParameter: LabTestParameter = {
      name: "",
      code: "",
      unit: "",
      dataType: "numeric",
      // rangeMin: null,
      // rangeMax: null,
      referenceText: null,
      referenceRanges: [],
      description: "",
      displayOrder: (test.parameters?.length || 0) + 1,
      isActive: true,
      labTestSectionId: sectionIndex !== undefined ? test.sections?.[sectionIndex]?.id || null : null,
    };
    setTest({
      ...test,
      parameters: [...(test.parameters || []), newParameter],
    });
  };

  const updateParameter = (index: number, field: keyof LabTestParameter, value: any) => {
    const updatedParameters = [...(test.parameters || [])];
    updatedParameters[index] = { ...updatedParameters[index], [field]: value };
    setTest({ ...test, parameters: updatedParameters });
    
    // Clear error for this field if it exists
    if (errors.parameters?.[index] && field === 'name') {
      const updatedErrors = { ...errors };
      if (updatedErrors.parameters?.[index]) {
        const paramErrors = { ...updatedErrors.parameters[index] };
        delete paramErrors.name;
        if (Object.keys(paramErrors).length === 0) {
          const newParameters = { ...updatedErrors.parameters };
          delete newParameters[index];
          updatedErrors.parameters = Object.keys(newParameters).length > 0 ? newParameters : undefined;
        } else {
          updatedErrors.parameters[index] = paramErrors;
        }
      }
      setErrors(updatedErrors);
    }
  };

  const removeParameter = (index: number) => {
    const updatedParameters = test.parameters?.filter((_, i) => i !== index) || [];
    // Update display orders
    updatedParameters.forEach((param, i) => {
      param.displayOrder = i + 1;
    });
    setTest({ ...test, parameters: updatedParameters });
  };

  const addParameterToSection = (sectionIndex: number) => {
    const section = test.sections?.[sectionIndex];
    if (!section) return;

    const newParameter: LabTestParameter = {
      name: "",
      code: "",
      unit: "",
      dataType: "numeric",
      // rangeMin: null,
      // rangeMax: null,
      referenceText: null,
      referenceRanges: [],
      description: "",
      displayOrder: (section.parameters?.length || 0) + 1,
      isActive: true,
      labTestSectionId: section.id || null,
    };

    const updatedSections = [...(test.sections || [])];
    updatedSections[sectionIndex] = {
      ...section,
      parameters: [...(section.parameters || []), newParameter],
    };
    setTest({ ...test, sections: updatedSections });
  };

  const updateSectionParameter = (
    sectionIndex: number,
    paramIndex: number,
    field: keyof LabTestParameter,
    value: any
  ) => {
    const updatedSections = [...(test.sections || [])];
    const section = updatedSections[sectionIndex];
    if (!section) return;

    const updatedParameters = [...(section.parameters || [])];
    updatedParameters[paramIndex] = {
      ...updatedParameters[paramIndex],
      [field]: value,
    };

    updatedSections[sectionIndex] = {
      ...section,
      parameters: updatedParameters,
    };
    setTest({ ...test, sections: updatedSections });
  };

  const removeSectionParameter = (sectionIndex: number, paramIndex: number) => {
    const updatedSections = [...(test.sections || [])];
    const section = updatedSections[sectionIndex];
    if (!section) return;

    const updatedParameters = section.parameters?.filter((_, i) => i !== paramIndex) || [];
    updatedParameters.forEach((param, i) => {
      param.displayOrder = i + 1;
    });

    updatedSections[sectionIndex] = {
      ...section,
      parameters: updatedParameters,
    };
    setTest({ ...test, sections: updatedSections });
  };

  const validateForm = (): boolean => {
    const newErrors: Errors = {};

    if (!test.name || test.name.trim() === "") {
      newErrors.name = "Test name is required";
    }

    if (test.defaultCharge < 0) {
      newErrors.defaultCharge = "Default charge cannot be negative";
    }

    // Validate sections
    test.sections?.forEach((section, index) => {
      if (!section.name || section.name.trim() === "") {
        if (!newErrors.sections) newErrors.sections = {};
        newErrors.sections[index] = { name: "Section name is required" };
      }
    });

    // Validate parameters (standalone and in sections)
    test.parameters?.forEach((param, index) => {
      if (!param.name || param.name.trim() === "") {
        if (!newErrors.parameters) newErrors.parameters = {};
        newErrors.parameters[index] = { name: "Parameter name is required" };
      }
    });

    test.sections?.forEach((section) => {
      section.parameters?.forEach((param, paramIndex) => {
        if (!param.name || param.name.trim() === "") {
          // Note: We're not tracking section parameter errors separately for simplicity
          // You can enhance this if needed
        }
      });
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const transformParameterToPayload = (param: LabTestParameter, sectionIndex?: number, isInlineParameter: boolean = false, isExistingSection: boolean = false): any => {
    const payload: any = {
      name: param.name,
      code: param.code || null,
      unit: param.unit || null,
      dataType: param.dataType || 'numeric',
      description: param.description || null,
      displayOrder: param.displayOrder || 0,
      isActive: param.isActive !== undefined ? param.isActive : true,
    };

    // Add id only if it exists (for existing parameters in edit mode)
    if (param.id) {
      payload.id = param.id;
    }

    // Handle section reference
    // For inline parameters in sections, backend will automatically set lab_test_section_id
    // So we don't need to set it in the payload
    if (!isInlineParameter) {
      // This is a top-level parameter
      if (sectionIndex !== undefined) {
        // For new sections, use sectionIndex
        payload.sectionIndex = sectionIndex;
      } else if (isExistingSection && param.labTestSectionId) {
        // For existing sections, use labTestSectionId
        payload.labTestSectionId = param.labTestSectionId;
      } else if (param.labTestSectionId) {
        // Top-level parameter referencing an existing section not in the sections array
        payload.labTestSectionId = param.labTestSectionId;
      }
    }

    // Transform reference ranges - use the new referenceRanges array if available
    const referenceRanges: any[] = [];
    
    if (param.referenceRanges && param.referenceRanges.length > 0) {
      // Use the new referenceRanges array
      param.referenceRanges.forEach((range) => {
        const rangePayload: any = {
          gender: range.gender || null,
          age_min: range.ageMin !== null && range.ageMin !== undefined ? Number(range.ageMin) : null,
          age_max: range.ageMax !== null && range.ageMax !== undefined ? Number(range.ageMax) : null,
          range_min: range.rangeMin !== null && range.rangeMin !== undefined ? Number(range.rangeMin) : null,
          range_max: range.rangeMax !== null && range.rangeMax !== undefined ? Number(range.rangeMax) : null,
          category_name: range.categoryName || null,
          remarks: range.remarks || null,
        };
        
        // Include id if it exists (for edit mode)
        if (range.id) {
          rangePayload.id = range.id;
        }
        
        referenceRanges.push(rangePayload);
      });
    } else {
      // Fallback to old format for backward compatibility
      if (param.dataType === 'numeric' && (param.referenceMin !== null && param.referenceMin !== undefined || 
          param.referenceMax !== null && param.referenceMax !== undefined)) {
        referenceRanges.push({
          gender: null,
          age_min: null,
          age_max: null,
          range_min: param.referenceMin !== null && param.referenceMin !== undefined ? Number(param.referenceMin) : null,
          range_max: param.referenceMax !== null && param.referenceMax !== undefined ? Number(param.referenceMax) : null,
          category_name: 'Normal',
          remarks: null,
        });
      } else if (param.dataType === 'text' && param.referenceText) {
        referenceRanges.push({
          gender: null,
          age_min: null,
          age_max: null,
          range_min: null,
          range_max: null,
          category_name: param.referenceText,
          remarks: null,
        });
      }
    }

    if (referenceRanges.length > 0) {
      payload.reference_ranges = referenceRanges;
    }

    return payload;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showNotification("error", "Please fix validation errors", "", "topRight");
      return;
    }

    try {
      setLoading(true);
      
      // Build sections payload with inline parameters
      const sectionsPayload = test.sections?.map((section, sectionIndex) => {
        const sectionData: any = {
          name: section.name,
          description: section.description || null,
          displayOrder: section.displayOrder || 0,
        };

        // Add id only if it exists (for existing sections in edit mode)
        if (section.id) {
          sectionData.id = section.id;
        }

        // Transform inline parameters in sections
        // Inline parameters don't need section reference as backend will set it automatically
        if (section.parameters && section.parameters.length > 0) {
          sectionData.parameters = section.parameters.map((param) => 
            transformParameterToPayload(param, sectionIndex, true)
          );
        }

        return sectionData;
      }) || [];

      // Build top-level parameters payload
      const topLevelParameters = (test.parameters || []).map((param) => {
        // Find section index if parameter references a section
        let sectionIndex: number | undefined = undefined;
        let isExistingSection = false;
        
        if (param.labTestSectionId && test.sections) {
          const sectionIdx = test.sections.findIndex(s => s.id === param.labTestSectionId);
          if (sectionIdx !== -1) {
            const section = test.sections[sectionIdx];
            // If section has an id, it's an existing section
            if (section.id) {
              isExistingSection = true;
            } else {
              // New section, use index
              sectionIndex = sectionIdx;
            }
          }
        }
        
        // Pass whether it's an existing section to the transform function
        return transformParameterToPayload(param, sectionIndex, false, isExistingSection);
      });

      const payload = {
        name: test.name,
        code: test.code || null,
        categoryId: test.categoryId || null,
        defaultCharge: test.defaultCharge || 0,
        description: test.description || null,
        sections: sectionsPayload,
        parameters: topLevelParameters,
      };

      if (isEditMode && id) {
        await updateLabTest(parseInt(id), payload);
        showNotification(
          "success",
          "Test updated successfully",
          "",
          "topRight"
        );
      } else {
        await saveLabTest(payload);
        showNotification(
          "success",
          "Test created successfully",
          "",
          "topRight"
        );
      }
      navigate("/test-list");
    } catch (err: any) {
      console.error("Failed to save test:", err);
      const errorMessage =
        err?.response?.data?.message ||
        err?.response?.data?.details?.message ||
        "Failed to save test";
      showNotification("error", "Error", errorMessage, "topRight");
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditMode) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">
            {isEditMode ? "Edit Test" : "Add Test"}
          </h1>
          <p className="mt-2 text-gray-600">
            {isEditMode
              ? "Update test information, sections, and parameters"
              : "Create a new laboratory test with sections and parameters"}
          </p>
        </div>
        <button
          onClick={() => navigate("/test-list")}
          className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Cancel
        </button>
      </div>

      <div className="p-6 bg-white rounded-lg shadow">
        {/* Basic Information Section */}
        <div className="mb-8">
          <h2 className="mb-4 text-lg font-medium text-gray-900">
            Basic Information
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium leading-6 text-gray-900">
                Test Name <span className="text-red-500">*</span>
              </label>
              <div className="mt-1">
                <input
                  type="text"
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  value={test.name}
                  onChange={(e) => updateTestField("name", e.target.value)}
                  placeholder="Enter test name"
                />
              </div>
              {errors.name && (
                <span className="text-sm text-red-500">{errors.name}</span>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium leading-6 text-gray-900">
                Test Code
              </label>
              <div className="mt-1">
                <input
                  type="text"
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  value={test.code || ""}
                  onChange={(e) => updateTestField("code", e.target.value)}
                  placeholder="Enter test code"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium leading-6 text-gray-900">
                Category
              </label>
              <div className="mt-1">
                <Select
                  style={{ width: "100%" }}
                  value={test.categoryId}
                  onChange={(value) => updateTestField("categoryId", value)}
                  placeholder="Select category"
                  allowClear
                  size="large"
                >
                  {categories.map((cat) => (
                    <Option key={cat.id} value={cat.id}>
                      {cat.name}
                    </Option>
                  ))}
                </Select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium leading-6 text-gray-900">
                Default Charge (₹) <span className="text-red-500">*</span>
              </label>
              <div className="mt-1">
                <InputNumber
                  style={{ width: "100%" }}
                  value={test.defaultCharge}
                  onChange={(value) => updateTestField("defaultCharge", value || 0)}
                  min={0}
                  step={0.01}
                  precision={2}
                  size="large"
                />
              </div>
              {errors.defaultCharge && (
                <span className="text-sm text-red-500">
                  {errors.defaultCharge}
                </span>
              )}
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-medium leading-6 text-gray-900">
                Description
              </label>
              <div className="mt-1">
                {/* <TextArea
                  rows={3}
                  value={test.description || ""}
                  onChange={(e: any) =>
                    updateTestField("description", e.target.value)
                  }
                  placeholder="Enter test description"
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                /> */}
                <Editor
                  editorState={descriptionEditorState}
                  placeholder="Enter test description"
                  onEditorStateChange={(state) => {
                    setDescriptionEditorState(state);
                    const raw = convertToRaw(state.getCurrentContent());
                    updateTestField("description", JSON.stringify(raw));
                  }}
                  wrapperClassName="border border-gray-300 rounded-md"
                  editorClassName="p-2 min-h-[80px]"
                  toolbarClassName="toolbar-class"
                  toolbar={{
                    options: ["inline", "list", "blockType"],
                    inline: { options: ["bold", "italic", "underline"] },
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Sections Section */}
        <div className="mb-8 border-t border-gray-200 pt-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900">Sections</h2>
            <button
              type="button"
              onClick={addSection}
              className="px-3 py-1.5 text-sm font-semibold text-white rounded-md bg-primary hover:bg-hover"
            >
              + Add Section
            </button>
          </div>

          {/* {test.sections?.map((section, sectionIndex) => ( */}
          {[...(test.sections || [])]
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .map((section, sectionIndex) => (
            <div
              key={sectionIndex}
              className="p-4 mb-4 border border-gray-200 rounded-lg"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium text-gray-900">
                  Section {sectionIndex + 1}
                </h3>
                <button
                  type="button"
                  onClick={() => removeSection(sectionIndex)}
                  className="px-2 py-1 text-sm text-red-600 hover:text-red-800"
                >
                  Remove
                </button>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium leading-6 text-gray-900">
                    Section Name <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                      value={section.name}
                      onChange={(e) =>
                        updateSection(sectionIndex, "name", e.target.value)
                      }
                      placeholder="Enter section name"
                    />
                  </div>
                  {errors.sections?.[sectionIndex]?.name && (
                    <span className="text-sm text-red-500">
                      {errors.sections[sectionIndex].name}
                    </span>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium leading-6 text-gray-900">
                    Display Order
                  </label>
                  <div className="mt-1">
                    <InputNumber
                      style={{ width: "100%" }}
                      value={section.displayOrder}
                      onChange={(value) =>
                        updateSection(sectionIndex, "displayOrder", value || 0)
                      }
                      min={0}
                      size="large"
                    />
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium leading-6 text-gray-900">
                    Description
                  </label>
                  <div className="mt-1">
                    <TextArea
                      rows={2}
                      value={section.description || ""}
                      onChange={(e: any) =>
                        updateSection(sectionIndex, "description", e.target.value)
                      }
                      placeholder="Enter section description"
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>
              </div>

              {/* Section Parameters */}
              <div className="mt-4 border-t border-gray-200 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-gray-700">
                    Parameters in this Section
                  </h4>
                  <button
                    type="button"
                    onClick={() => addParameterToSection(sectionIndex)}
                    className="px-2 py-1 text-xs font-semibold text-white rounded bg-indigo-600 hover:bg-indigo-700"
                  >
                    + Add Parameter
                  </button>
                </div>

                {/* {section.parameters?.map((param, paramIndex) => ( */}
                  {[...(section.parameters || [])]
                    .sort((a, b) => a.displayOrder - b.displayOrder)
                    .map((param, paramIndex) => (
                  <div
                    key={paramIndex}
                    className="p-3 mb-3 bg-gray-50 rounded border border-gray-200"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">
                        Parameter {paramIndex + 1}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          removeSectionParameter(sectionIndex, paramIndex)
                        }
                        className="text-xs text-red-600 hover:text-red-800"
                      >
                        Remove
                      </button>
                    </div>
                    <ParameterForm
                      parameter={param}
                      onUpdate={(field, value) =>
                        updateSectionParameter(sectionIndex, paramIndex, field, value)
                      }
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Standalone Parameters Section */}
        <div className="mb-8 border-t border-gray-200 pt-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900">
              Parameters (Not in Sections)
            </h2>
            <button
              type="button"
              onClick={() => addParameter()}
              className="px-3 py-1.5 text-sm font-semibold text-white rounded-md bg-primary hover:bg-hover"
            >
              + Add Parameter
            </button>
          </div>

          {/* {test.parameters?.map((param, paramIndex) => ( */}
          {[...(test.parameters || [])]
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .map((param, paramIndex) => (
            <div
              key={paramIndex}
              className="p-4 mb-4 border border-gray-200 rounded-lg"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium text-gray-900">
                  Parameter {paramIndex + 1}
                </h3>
                <button
                  type="button"
                  onClick={() => removeParameter(paramIndex)}
                  className="px-2 py-1 text-sm text-red-600 hover:text-red-800"
                >
                  Remove
                </button>
              </div>
              <ParameterForm
                parameter={param}
                onUpdate={(field, value) =>
                  updateParameter(paramIndex, field, value)
                }
              />
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={() => navigate("/test-list")}
            className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={loading}
            className="px-4 py-2 text-sm font-semibold text-white rounded-md bg-primary hover:bg-hover disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Saving..." : isEditMode ? "Update Test" : "Save Test"}
          </button>
        </div>
      </div>
    </div>
  );
};

// Parameter Form Component
const ParameterForm: React.FC<{
  parameter: LabTestParameter;
  onUpdate: (field: keyof LabTestParameter, value: any) => void;
}> = ({ parameter, onUpdate }) => {

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      <div>
        <label className="block text-sm font-medium leading-6 text-gray-900">
          Parameter Name <span className="text-red-500">*</span>
        </label>
        <div className="mt-1">
          <input
            type="text"
            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            value={parameter.name}
            onChange={(e) => onUpdate("name", e.target.value)}
            placeholder="Enter parameter name"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium leading-6 text-gray-900">
          Code
        </label>
        <div className="mt-1">
          <input
            type="text"
            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            value={parameter.code || ""}
            onChange={(e) => onUpdate("code", e.target.value)}
            placeholder="Enter parameter code"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium leading-6 text-gray-900">
          Unit
        </label>
        <div className="mt-1">
          <input
            type="text"
            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            value={parameter.unit || ""}
            onChange={(e) => onUpdate("unit", e.target.value)}
            placeholder="e.g., mg/dL, mmol/L"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium leading-6 text-gray-900">
          Data Type <span className="text-red-500">*</span>
        </label>
        <div className="mt-1">
          <Select
            style={{ width: "100%" }}
            value={parameter.dataType}
            onChange={(value) => onUpdate("dataType", value)}
            size="large"
          >
            <Option value="numeric">Numeric</Option>
            <Option value="text">Text</Option>
            <Option value="boolean">Boolean</Option>
            <Option value="date">Date</Option>
          </Select>
        </div>
      </div>

      {/* Reference Ranges Section */}
      <div className="sm:col-span-2 lg:col-span-3">
        <div className="flex items-center justify-between mb-3">
          <label className="block text-sm font-medium leading-6 text-gray-900">
            Reference Ranges
          </label>
          <button
            type="button"
            onClick={() => {
              const currentRanges = parameter.referenceRanges || [];
              const newRange: ReferenceRange = {
                id: undefined,
                gender: null,
                ageMin: null,
                ageMax: null,
                rangeMin: null,
                rangeMax: null,
                categoryName: null,
                remarks: null,
              };
              onUpdate("referenceRanges", [...currentRanges, newRange]);
            }}
            className="px-2 py-1 text-xs font-semibold text-white rounded bg-indigo-600 hover:bg-indigo-700"
          >
            + Add Range
          </button>
        </div>

        {(parameter.referenceRanges || []).map((range, rangeIndex) => (
          <div
            key={rangeIndex}
            className="p-4 mb-3 bg-gray-50 rounded border border-gray-200"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-700">
                Range {rangeIndex + 1}
              </span>
              <button
                type="button"
                onClick={() => {
                  const currentRanges = parameter.referenceRanges || [];
                  const updatedRanges = currentRanges.filter((_, i) => i !== rangeIndex);
                  onUpdate("referenceRanges", updatedRanges);
                }}
                className="text-xs text-red-600 hover:text-red-800"
              >
                Remove
              </button>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <label className="block text-xs font-medium leading-6 text-gray-700">
                  Gender
                </label>
                <div className="mt-1">
                  <Select
                    style={{ width: "100%" }}
                    value={range.gender}
                    onChange={(value) => {
                      const currentRanges = parameter.referenceRanges || [];
                      const updatedRanges = [...currentRanges];
                      updatedRanges[rangeIndex] = { ...range, gender: value };
                      onUpdate("referenceRanges", updatedRanges);
                    }}
                    placeholder="Select gender"
                    allowClear
                    size="small"
                  >
                    <Option value="male">Male</Option>
                    <Option value="female">Female</Option>
                    <Option value="other">Other</Option>
                  </Select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium leading-6 text-gray-700">
                  Age Min
                </label>
                <div className="mt-1">
                  <InputNumber
                    style={{ width: "100%" }}
                    value={range.ageMin}
                    onChange={(value) => {
                      const currentRanges = parameter.referenceRanges || [];
                      const updatedRanges = [...currentRanges];
                      updatedRanges[rangeIndex] = { ...range, ageMin: value !== null && value !== undefined ? Number(value) : null };
                      onUpdate("referenceRanges", updatedRanges);
                    }}
                    min={0}
                    size="small"
                    placeholder="Min age"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium leading-6 text-gray-700">
                  Age Max
                </label>
                <div className="mt-1">
                  <InputNumber
                    style={{ width: "100%" }}
                    value={range.ageMax}
                    onChange={(value) => {
                      const currentRanges = parameter.referenceRanges || [];
                      const updatedRanges = [...currentRanges];
                      updatedRanges[rangeIndex] = { ...range, ageMax: value !== null && value !== undefined ? Number(value) : null };
                      onUpdate("referenceRanges", updatedRanges);
                    }}
                    min={0}
                    size="small"
                    placeholder="Max age"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium leading-6 text-gray-700">
                  Range Min
                </label>
                <div className="mt-1">
                  <InputNumber
                    style={{ width: "100%" }}
                    value={range.rangeMin}
                    onChange={(value) => {
                      const currentRanges = parameter.referenceRanges || [];
                      const updatedRanges = [...currentRanges];
                      updatedRanges[rangeIndex] = { ...range, rangeMin: value !== null && value !== undefined ? Number(value) : null };
                      onUpdate("referenceRanges", updatedRanges);
                    }}
                    step={0.01}
                    precision={2}
                    size="small"
                    placeholder="Min value"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium leading-6 text-gray-700">
                  Range Max
                </label>
                <div className="mt-1">
                  <InputNumber
                    style={{ width: "100%" }}
                    value={range.rangeMax}
                    onChange={(value) => {
                      const currentRanges = parameter.referenceRanges || [];
                      const updatedRanges = [...currentRanges];
                      updatedRanges[rangeIndex] = { ...range, rangeMax: value !== null && value !== undefined ? Number(value) : null };
                      onUpdate("referenceRanges", updatedRanges);
                    }}
                    step={0.01}
                    precision={2}
                    size="small"
                    placeholder="Max value"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium leading-6 text-gray-700">
                  Category Name 
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    value={range.categoryName || ""}
                    onChange={(e) => {
                      const currentRanges = parameter.referenceRanges || [];
                      const updatedRanges = [...currentRanges];
                      updatedRanges[rangeIndex] = { ...range, categoryName: e.target.value || null };
                      onUpdate("referenceRanges", updatedRanges);
                    }}
                    placeholder="Enter category name"
                  />
                </div>
              </div>

              <div className="sm:col-span-2 lg:col-span-3">
                <label className="block text-xs font-medium leading-6 text-gray-700">
                  Remarks
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    value={range.remarks || ""}
                    onChange={(e) => {
                      const currentRanges = parameter.referenceRanges || [];
                      const updatedRanges = [...currentRanges];
                      updatedRanges[rangeIndex] = { ...range, remarks: e.target.value || null };
                      onUpdate("referenceRanges", updatedRanges);
                    }}
                    placeholder="Enter remarks"
                  />
                </div>
              </div>
            </div>
          </div>
        ))}

        {(!parameter.referenceRanges || parameter.referenceRanges.length === 0) && (
          <div className="text-sm text-gray-500 italic">
            No reference ranges added. Click "Add Range" to add one.
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium leading-6 text-gray-900">
          Display Order
        </label>
        <div className="mt-1">
          <InputNumber
            style={{ width: "100%" }}
            value={parameter.displayOrder}
            onChange={(value) => onUpdate("displayOrder", value || 0)}
            min={0}
            size="large"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium leading-6 text-gray-900">
          Active
        </label>
        <div className="mt-1">
          <Switch
            checked={parameter.isActive}
            onChange={(checked) => onUpdate("isActive", checked)}
          />
        </div>
      </div>

      <div className="sm:col-span-2 lg:col-span-3">
        <label className="block text-sm font-medium leading-6 text-gray-900">
          Description
        </label>
        <div className="mt-1">
          <TextArea
            rows={2}
            value={parameter.description || ""}
            onChange={(e: any) => onUpdate("description", e.target.value)}
            placeholder="Enter parameter description"
            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
          />
        </div>
      </div>
    </div>
  );
};

export default AddTest;

